import { Button, Callout } from '@aecom-ai-engineering/react-components';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

export type ProjectOverviewErrorVariant = 'no-access' | 'unavailable';

interface ProjectOverviewErrorProps {
  variant: ProjectOverviewErrorVariant;
  /** Refetches the project. Only the `unavailable` arm offers it. */
  onRetry: () => void;
  /** Disables the retry while the refetch it started is still in flight. */
  isRetrying: boolean;
}

/**
 * What the page shows when `getProject` fails.
 *
 * Deliberately not the global `ErrorPage`: a 403 on a project is an expected
 * outcome — a shared link, revoked access, a mistyped id — not a crash, and the
 * crash UI answers it with a redacted message and a "send feedback" button.
 * This also renders inside `AppShell`, so the shell stays and only the header
 * and tab body are replaced, which is what keeps the page off a blank title.
 *
 * 403 and 404 collapse into one arm on purpose. Telling an unauthorised caller
 * that a project *exists* is the leak monopy's `Http404` is designed to avoid —
 * it already returns 404 for a project you cannot view, so distinguishing them
 * here would undo that.
 */
export const ProjectOverviewError: FC<ProjectOverviewErrorProps> = ({
  variant,
  onRetry,
  isRetrying,
}) => {
  const { t } = useTranslation();

  if (variant === 'no-access') {
    // The existing no-access copy rather than new: this is the same sentence
    // the invite flow says, and it is already translated in all 11 locales.
    // No retry — retrying an authorisation decision does not change it.
    return <Callout variant="warning">{t('invite-user.contact-admin')}</Callout>;
  }

  return (
    <div className="flex flex-col items-start gap-4">
      <Callout className="w-full" variant="negative">
        {t('project-overview.project-unavailable')}
      </Callout>
      <Button disabled={isRetrying} onClick={onRetry} size="md" variant="outline">
        {t('project-overview.retry')}
      </Button>
    </div>
  );
};
